/* ============================================================
   LATEST SPACE NEWS — live carousel.
   Articles are fetched from the Spaceflight News API (v4) — the
   same source used elsewhere in the app — and mapped onto the
   section's CSS "scene" cards. The curated NEWS list seeds the
   first paint (SSR) and is the fallback if the request fails.
   ============================================================ */
import { useEffect, useRef, useState } from "react";
import { NEWS, NewsItem } from "../../../utils/niftyData";

type NewsCard = NewsItem & { url?: string; image?: string };

const SCENES = ["station", "launch", "earth", "marsmoon", "exo", "lunar"];
const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return `${String(d.getUTCDate()).padStart(2, "0")} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** Derive a display tag + matching CSS scene from the article text. */
function categorize(text: string, index: number): { tag: string; scene: string } {
  const t = text.toLowerCase();
  if (/launch|rocket|booster|ariane|falcon|starship|soyuz|liftoff|satellite/.test(t)) return { tag: "LAUNCH", scene: "launch" };
  if (/week in images|imagery|gallery|photo|picture|image of/.test(t)) return { tag: "IMAGERY", scene: "earth" };
  if (/moon|lunar|artemis|viper|south pole|rover/.test(t)) return { tag: "MISSION", scene: "lunar" };
  if (/mars|martian/.test(t)) return { tag: "MISSION", scene: "marsmoon" };
  if (/webb|telescope|exoplanet|atmosphere|galaxy|black hole|nebula|astronom|water vapou?r|discover|study|research/.test(t)) return { tag: "SCIENCE", scene: "exo" };
  if (/fund|million|billion|invest|valuation|contract|deal|acquir|revenue|ipo|startup|company|industry/.test(t)) return { tag: "INDUSTRY", scene: "station" };
  if (/opinion|editorial|column|perspective|let's|why /.test(t)) return { tag: "OPINION", scene: "marsmoon" };
  return { tag: "ORBIT", scene: SCENES[index % SCENES.length] };
}

export default function SpaceNews() {
  const [news, setNews] = useState<NewsCard[]>(NEWS);
  const trackRef = useRef<HTMLDivElement>(null);

  // Pull the latest live articles and map them onto the scene cards.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch("https://api.spaceflightnewsapi.net/v4/articles/?limit=12");
        if (!res.ok) return;
        const data = await res.json();
        const results: any[] = Array.isArray(data?.results) ? data.results : [];
        const mapped: NewsCard[] = results
          .filter((a) => a?.title && a?.url)
          .slice(0, 9)
          .map((a, i) => {
            const cat = categorize(`${a.title} ${a.summary || ""}`, i);
            return {
              tag: cat.tag,
              scene: cat.scene,
              title: a.title as string,
              src: String(a.news_site || "SPACE WIRE").toUpperCase(),
              date: formatDate(a.published_at),
              url: a.url as string,
              image: typeof a.image_url === "string" && a.image_url.trim() ? a.image_url : undefined,
            };
          });
        if (alive && mapped.length) setNews(mapped);
      } catch {
        /* network/API error — keep the curated fallback */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const scrollBy = (dir: number) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>(".news-card");
    const amt = card ? card.offsetWidth + 20 : 320;
    el.scrollBy({ left: dir * amt, behavior: "smooth" });
  };

  return (
    <section id="news" className="sec-pad news-sec" data-screen-label="Space News">
      <div className="wrap">
        <div className="news-head">
          <div>
            <div className="sec-tag"><span className="idx">[ 06 ]</span> DISPATCHES <span className="rule"></span> <span>FROM ORBIT &amp; BEYOND</span></div>
            <h2 className="h-md news-title" data-reveal><span className="news-bar"></span>Latest Space News</h2>
          </div>
          <div className="news-nav">
            <button className="news-arrow" aria-label="Previous" onClick={() => scrollBy(-1)}>‹</button>
            <button className="news-arrow" aria-label="Next" onClick={() => scrollBy(1)}>›</button>
          </div>
        </div>
        <div className="news-track" ref={trackRef}>
          {news.map((n, i) => (
            <a
              className="news-card panel panel--hover"
              key={(n.url || n.title) + i}
              href={n.url || `https://news.google.com/search?q=${encodeURIComponent(n.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${n.title} — read more (${n.src})`}
            >
              <div className="news-media">
                <div className={"news-scene scene--" + n.scene}>
                  <span className="news-stars"></span>
                  <span className="news-planet"></span>
                  {(n.scene === "marsmoon") && <span className="news-moon"></span>}
                  {(n.scene === "exo") && <span className="news-ring"></span>}
                  {(n.scene === "station" || n.scene === "launch") && <span className="news-orbit"></span>}
                </div>
                {n.image && (
                  // Real article image layered over the CSS scene (the scene shows
                  // through as a fallback if the image is missing or fails to load).
                  <img
                    className="news-img"
                    src={n.image}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                )}
                <span className="news-badge mono">{n.tag}</span>
              </div>
              <div className="news-body">
                <div className="news-meta mono"><span>{n.src}</span><span>{n.date}</span></div>
                <h3 className="news-h">{n.title}</h3>
                <span className="news-more mono">READ MORE <span className="ar">→</span></span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
